import type { SearchParams, SearchResponse } from "../../api/types";
import { useSearch } from "../../hooks/useSearch";
import { Loading, EmptyState, ErrorState } from "../Spinner";
import { SearchBar } from "./SearchBar";
import { Facets } from "./Facets";
import { SortControl } from "./SortControl";
import { Pagination } from "./Pagination";
import { ResultCard } from "./ResultCard";

/**
 * The complete faceted-search experience: search bar, facet sidebar, results
 * meta (count + sort), the uniform results list, and pagination — all wired to
 * the shared `useSearch` hook.
 *
 * This is the single implementation behind BOTH the blog feed and the
 * site-wide search page. They differ only in the `fetcher` they pass:
 *   - Blog feed  → getBlogFeed (everything except photographs)
 *   - Search     → search       (every content type)
 *
 * Keeping one component means search behaviour, accessibility, and styling stay
 * identical everywhere they appear.
 */
interface SearchExperienceProps {
  /** Endpoint the search runs against. */
  fetcher: (params: SearchParams) => Promise<SearchResponse>;
  /** Seed the query box (e.g. from a ?q= URL param). */
  initialQuery?: string;
  placeholder?: string;
  /** Message shown when a search returns nothing. */
  emptyLabel?: string;
}

export function SearchExperience({
  fetcher,
  initialQuery = "",
  placeholder,
  emptyLabel = "No entries match your search yet.",
}: SearchExperienceProps) {
  const {
    query,
    types,
    tags,
    collections,
    year,
    sort,
    page,
    response,
    loading,
    error,
    setQuery,
    toggleType,
    toggleTag,
    toggleCollection,
    setYear,
    setSort,
    setPage,
    clearFilters,
  } = useSearch(fetcher, { initialQuery });

  const total = response?.totalItems ?? 0;
  const items = response?.items ?? [];

  return (
    <div className="stack" style={{ gap: "var(--space-4)" }}>
      <SearchBar value={query} onChange={setQuery} placeholder={placeholder} />

      <div className="search-shell">
        <Facets
          aggregations={response?.aggregations ?? null}
          selectedTypes={types}
          selectedTags={tags}
          selectedCollections={collections}
          selectedYear={year}
          onToggleType={toggleType}
          onToggleTag={toggleTag}
          onToggleCollection={toggleCollection}
          onSelectYear={setYear}
          onClear={clearFilters}
        />

        <div>
          <div className="results-meta">
            <span className="results-meta__count">
              <b>{total}</b> {total === 1 ? "entry" : "entries"}
              {query ? ` for “${query}”` : ""}
              {year ? ` from ${year}` : ""}
            </span>
            <SortControl value={sort} onChange={setSort} />
          </div>

          {error ? (
            <ErrorState label={error} />
          ) : loading && items.length === 0 ? (
            <Loading label="Searching the archive…" />
          ) : items.length === 0 ? (
            <EmptyState label={emptyLabel} />
          ) : (
            <>
              <div className="results" aria-busy={loading}>
                {items.map((item) => (
                  <ResultCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination
                page={page}
                totalPages={response?.totalPages ?? 1}
                onChange={setPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
